import { cn } from '@listening/utils/react-helpers';
import { menuSurface } from '@/components/web-fe/design-v2/surfaces';
import { Skeleton } from '@listening/frontend/components/radix/skeleton';
import PlanIcon from '@listening/assets/icons/plan.svg?react';
import * as m from '@/paraglide/messages';

import { Button } from '@listening/frontend/components/radix/button';
import { Calendar } from '@listening/frontend/components/radix/calendar';
import { DialogTrigger } from '@listening/frontend/components/radix/dialog';
import { DialogCloseProvider } from '@listening/frontend/components/custom/dialog-dropdown';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@listening/frontend/components/radix/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@listening/frontend/components/radix/select';
import { Toggle } from '@listening/frontend/components/radix/toggle';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'iconoir-react';
import { ExitFlowDialog } from '@/components/web-fe/dialogs/cancel-delete-account-flow/cancel-account-dialog';
import { DashboardPage } from '@/components/web-fe/pages/dashboard/dashboard-page';
import { ExpiringPlanWarning } from '@/components/web-fe/plan/expiring-subscription-warning';
import { PlanPicker } from '@/components/web-fe/plan/plan-picker';
import { YourPlanSection } from '@/components/web-fe/plan/your-plan-section';
import { useAnalytics, useFlag } from '@listening/frontend/analytics/client';
import {
  paymentDetailsQueryKey,
  paymentSubscriptionsQueryKey,
  userSummaryQueryKey,
} from '@listening/frontend/query/user';
import { useMockSubStore } from '@/services/mock-api-responses';
import { useBillingStatusQuery } from '@listening/frontend/query/user-hooks';

export const Route = createFileRoute('/app/_authenticated/_dashboard/plan')({
  component: PlanPage,
  context: () => ({
    title: m.common_plan(),
    icon: PlanIcon,
  }),
});

function PlanPage() {
  const { value: mockSubFlag } = useFlag('mockSubscriptionEndpoint');
  const statusQuery = useBillingStatusQuery();

  if (statusQuery.isPending) {
    return <PlanPageSkeleton />;
  }

  return (
    <div className='relative h-full'>
      {mockSubFlag === 'true' && <DevMockSub />}
      <DashboardPage
        header={{
          Icon: PlanIcon,
          title: m.common_plan(),
        }}
      >
        <div className='mx-auto mt-8 flex w-full max-w-2xl flex-col gap-8 px-4 pb-24'>
          <div className='flex flex-col items-center gap-2 text-center'>
            <h1 className='text-3xl font-bold text-[var(--v2-text-primary)]'>
              {m.plan_manage()}
            </h1>
            <p className='text-base text-steel-880 dark:text-slate-400'>
              {m.plan_manage_description()}
            </p>
          </div>

          <div className='flex flex-col gap-8'>
            <YourPlanSection />
            <UpgradePlanCard />
            <ExpiringPlanWarning />
            <CancelSubscriptionButton />
          </div>
        </div>
      </DashboardPage>
    </div>
  );
}

function PlanPageSkeleton() {
  return (
    <DashboardPage
      header={{
        Icon: PlanIcon,
        title: m.common_plan(),
      }}
    >
      <div className='mx-auto mt-8 flex w-full max-w-2xl flex-col gap-8 px-4 pb-24'>
        <div className='flex flex-col items-center gap-2 text-center'>
          <Skeleton className='h-9 w-52' />
          <Skeleton className='h-5 w-80' />
        </div>

        <div className='flex flex-col gap-8'>
          <div className='flex flex-col gap-6 rounded-[32px] border border-white/20 bg-white/60 p-8 shadow-xl shadow-indigo-100/20 dark:border-white/10 dark:bg-slate-900/60 dark:shadow-none'>
            <div className='flex flex-col gap-6 md:flex-row md:justify-between'>
              <div className='flex flex-col gap-2'>
                <Skeleton className='h-4 w-24' />
                <Skeleton className='h-8 w-40' />
                <Skeleton className='h-4 w-32' />
              </div>
              <div className='flex flex-col gap-2 md:items-end'>
                <Skeleton className='h-3 w-20' />
                <Skeleton className='h-5 w-28' />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-6 rounded-[32px] border border-white/20 bg-white/60 p-8 shadow-xl shadow-indigo-100/20 dark:border-white/10 dark:bg-slate-900/60 dark:shadow-none'>
            <div>
              <Skeleton className='mb-2 h-7 w-36' />
              <Skeleton className='h-4 w-56' />
            </div>
            <div className='flex gap-4'>
              <Skeleton className='h-40 flex-1 rounded-xl' />
              <Skeleton className='h-40 flex-1 rounded-xl' />
            </div>
          </div>
        </div>
      </div>
    </DashboardPage>
  );
}

function CancelSubscriptionButton() {
  const statusQuery = useBillingStatusQuery();
  const { capture } = useAnalytics();

  // One boolean, decided server-side. It is false for a store subscriber, for a
  // subscription already ending, and for the shapes where cancelling is not a
  // single call — the client no longer reconstructs any of that from statuses.
  const showCancelSubscription =
    statusQuery.data?.billing.can_cancel_in_app === true;

  if (!showCancelSubscription) return null;

  return (
    <div className='mt-8 flex justify-center'>
      <DialogCloseProvider>
        <DialogTrigger asChild>
          <Button
            variant='ghost'
            onClick={() => {
              capture('cancel_subscription_clicked', null);
            }}
            className='h-auto rounded-full px-6 py-2.5 text-sm font-medium text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-gray-500 dark:hover:bg-red-950/20 dark:hover:text-red-400'
          >
            {m.plan_cancel_subscription()}
          </Button>
        </DialogTrigger>
        <ExitFlowDialog isDeleteAccount={false} />
      </DialogCloseProvider>
    </div>
  );
}

function DevMockSub() {
  const mockStore = useMockSubStore();
  const queryClient = useQueryClient();
  return (
    <div className='absolute top-2 flex gap-2'>
      <Select
        value={mockStore.chosenMock}
        onValueChange={(m) => {
          mockStore.setChosenMock(
            m as Parameters<typeof mockStore.setChosenMock>[0],
          );
          return queryClient.refetchQueries({
            queryKey: paymentSubscriptionsQueryKey,
          });
        }}
      >
        <SelectTrigger className='w-[180px]'>
          <SelectValue placeholder={m.plan_mock_subscription()} />
        </SelectTrigger>
        <SelectContent className={menuSurface}>
          {mockStore.possibleMocks.map((m) => (
            <SelectItem key={m} value={m}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <DatePicker
        dateState={[
          mockStore.mockNextPaymentDate,
          (date) => {
            void queryClient.refetchQueries({
              queryKey: paymentDetailsQueryKey,
            });
            mockStore.setMockNextPaymentDate(date);
          },
        ]}
      />
      <Toggle
        pressed={mockStore.isSubscriptionValid}
        onPressedChange={(value) => {
          void queryClient.refetchQueries({
            queryKey: userSummaryQueryKey,
          });
          mockStore.setIsSubscriptionValid(value);
        }}
      >
        {mockStore.isSubscriptionValid
          ? m.plan_valid_subscription()
          : m.plan_invalid_subscription()}
      </Toggle>
    </div>
  );
}

function UpgradePlanCard() {
  return (
    <section className='flex flex-col gap-6 rounded-[32px] border border-white/20 bg-white/60 p-8 shadow-xl shadow-indigo-100/20 dark:border-white/10 dark:bg-slate-900/60 dark:shadow-none'>
      <div className='flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center'>
        <div>
          <h2 className='text-2xl font-bold text-[var(--v2-text-primary)]'>
            {m.plan_upgrade()}
          </h2>
          <p className='text-sm text-steel-880 dark:text-slate-400'>
            {m.plan_upgrade_description()}
          </p>
        </div>
      </div>
      <PlanPicker showHeading={false} />
    </section>
  );
}

function DatePicker({
  dateState,
}: {
  dateState: [Date | undefined, (d: Date | undefined) => void];
}) {
  const [date, setDate] = dateState;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          className={cn(
            'w-[280px] justify-start text-left font-normal',
            !date && 'text-muted-foreground',
          )}
        >
          <CalendarIcon />
          {date ? format(date, 'PPP') : <span>{m.plan_pick_date()}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-auto p-0', menuSurface)}>
        <Calendar mode='single' selected={date} onSelect={setDate} />
      </PopoverContent>
    </Popover>
  );
}
